import { apiService } from './api';

export interface Department {
  departmentId: number;
  departmentCode: string;
  departmentName: string;
  departmentShortName: string;
  parentDepartmentId?: number;
  parentDepartmentName: string;
  departmentType: string;
  departmentHeadId?: number;
  departmentHeadName: string;
  description?: string;
  emailAddress?: string;
  contactNumber?: string;
  extensionNumber?: string;
  location?: string;
  costCenterCode?: string;
  budgetAmount?: number;
  employeeCount?: number;
  isActive: boolean;
  createdDate: string;
}

export interface CreateDepartmentDTO {
  departmentCode: string;
  departmentName: string;
  departmentShortName: string;
  parentDepartmentId?: number;
  departmentType: string;
  departmentHeadId?: number;
  description?: string;
  emailAddress?: string;
  contactNumber?: string;
  extensionNumber?: string;
  location?: string;
  costCenterCode?: string;
  budgetAmount?: number;
  employeeCount?: number;
}

export interface UpdateDepartmentDTO {
  departmentCode: string;
  departmentName: string;
  departmentShortName: string;
  parentDepartmentId?: number;
  departmentType: string;
  departmentHeadId?: number;
  description?: string;
  emailAddress?: string;
  contactNumber?: string;
  extensionNumber?: string;
  location?: string;
  costCenterCode?: string;
  budgetAmount?: number;
  employeeCount?: number;
  isActive: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

class DepartmentService {
  async getDepartments(pageNumber: number = 1, pageSize: number = 50) {
    try {
      const response = await apiService.get<PaginatedResponse<Department>>(
        '/departments',
        { pageNumber, pageSize }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  }

  async getDepartmentById(id: number) {
    try {
      const response = await apiService.get<Department>(`/departments/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching department:', error);
      throw error;
    }
  }

  async getDepartmentByCode(code: string) {
    try {
      const response = await apiService.get<Department>(`/departments/code/${code}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching department by code:', error);
      throw error;
    }
  }

  async createDepartment(dto: CreateDepartmentDTO) {
    try {
      const response = await apiService.post<Department>('/departments', dto);
      return response.data.data;
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  }

  async updateDepartment(id: number, dto: UpdateDepartmentDTO) {
    try {
      const response = await apiService.put<Department>(`/departments/${id}`, dto);
      return response.data.data;
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  }

  async deleteDepartment(id: number) {
    try {
      await apiService.delete(`/departments/${id}`);
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  }

  async getChildDepartments(parentId: number) {
    try {
      const response = await apiService.get<Department[]>(`/departments/${parentId}/children`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching child departments:', error);
      throw error;
    }
  }
}

const departmentService = new DepartmentService();
export default departmentService;
